import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isTokenExpiring } from "../utils/authUtils";

/**
 * ProtectedRoute - Redirects to login if user is not authenticated or token is expired.
 * Stores the attempted URL to redirect back after login.
 */
const ProtectedRoute = ({
  children,
  allowedRoles = null,
  requireActiveRoleMatch = false,
  denyDualRoleR1R3 = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isValidSession = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return false;
    // isTokenExpiring(0) returns true when the token is expired right now
    if (isTokenExpiring(0)) {
      // Clear stale token so subsequent checks are clean
      localStorage.removeItem("ogestyle_token");
      localStorage.removeItem("ogestyle_token_expires");
      return false;
    }
    return true;
  };

  const isAllowedRole = () => {
    const currentRole =
      localStorage.getItem("ogestyle_role_override") ||
      localStorage.getItem("ogestyle_role");
    const rolesList = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");

    const hasDualOwnerProfessionalRoles =
      rolesList.includes("R1") && rolesList.includes("R3");
    if (denyDualRoleR1R3 && hasDualOwnerProfessionalRoles) {
      return false;
    }

    if (!allowedRoles || allowedRoles.length === 0) return true;

    if (requireActiveRoleMatch) {
      return Boolean(currentRole && allowedRoles.includes(currentRole));
    }

    return (
      (currentRole && allowedRoles.includes(currentRole)) ||
      rolesList.some((role) => allowedRoles.includes(role))
    );
  };

  useEffect(() => {
    if (!isValidSession()) {
      // Store the attempted URL to redirect back after login
      sessionStorage.setItem(
        "redirectAfterLogin",
        location.pathname + location.search,
      );
      navigate("/login", { replace: true });
    } else if (!isAllowedRole()) {
      navigate("/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location, allowedRoles]);

  return isValidSession() && isAllowedRole() ? children : null;
};

export default ProtectedRoute;
